// src/components/map/TwoVariableLegendEditor.jsx
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Save, RotateCcw, Palette, Ruler } from "lucide-react";
import PropTypes from "prop-types";

/**
 * TwoVariableLegendEditor - Specialized editor for 2-variable maps
 * Features a split button to switch between editing color legend and size legend
 */
const TwoVariableLegendEditor = ({
  isOpen,
  onClose,
  layerConfig,
  onConfigChange,
  onSave,
  onPreview, // Added for live preview support
}) => {
  // State for active editing mode: 'color' or 'size'
  const [editMode, setEditMode] = useState("color");

  // State for legend properties
  const [legendTitle, setLegendTitle] = useState("");
  const [colorLabel, setColorLabel] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [colorLegendTitle, setColorLegendTitle] = useState(""); // Custom title for color legend section
  const [sizeLegendTitle, setSizeLegendTitle] = useState(""); // Custom title for size legend section
  const [colorPrefix, setColorPrefix] = useState("");
  const [colorSuffix, setColorSuffix] = useState("");
  const [colorDecimals, setColorDecimals] = useState(2);
  const [sizePrefix, setSizePrefix] = useState("");
  const [sizeSuffix, setSizeSuffix] = useState("");
  const [sizeDecimals, setSizeDecimals] = useState(2);
  const [isSaving, setIsSaving] = useState(false);
  const [colorLegendItems, setColorLegendItems] = useState([]);
  const [sizeLegendItems, setSizeLegendItems] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Store original values for reset functionality
  const originalValuesRef = useRef({});

  // Check if this is truly a 2-variable map
  const is2VariableMap =
    layerConfig?.colorClassBreakInfos?.length > 0 &&
    layerConfig?.sizeClassBreakInfos?.length > 0 &&
    layerConfig?.valueColumn1 &&
    layerConfig?.valueColumn2;

  // Format value based on current formatting settings
  const formatValue = (value, mode, includeSuffix = true) => {
    if (value === undefined || value === null || typeof value !== "number")
      return value || "";

    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    const decimals = mode === "color" ? colorDecimals : sizeDecimals;
    const prefix = mode === "color" ? colorPrefix : sizePrefix;
    const suffix = mode === "color" ? colorSuffix : sizeSuffix;

    let formattedValue = "";

    // Apply decimal formatting
    if (decimals === 0) {
      formattedValue = Math.round(numValue).toLocaleString();
    } else {
      formattedValue = numValue.toFixed(decimals);
    }

    // Apply prefix and suffix (no automatic spacing - users add spaces if needed)
    return `${prefix}${formattedValue}${includeSuffix && suffix ? suffix : ''}`;
  };

  // Extract legend items from config
  const extractLegendItems = useCallback((config) => {
    if (!config) return { colorItems: [], sizeItems: [] };

    const colorItems = [];
    const sizeItems = [];

    // Extract color breaks
    if (config.colorClassBreakInfos) {
      config.colorClassBreakInfos.forEach((breakInfo, index) => {
        colorItems.push({
          id: index,
          label: breakInfo.label || `Break ${index + 1}`,
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          color: breakInfo.symbol?.color,
          editable: true,
        });
      });
    }

    // Extract size breaks
    if (config.sizeClassBreakInfos) {
      config.sizeClassBreakInfos.forEach((breakInfo, index) => {
        sizeItems.push({
          id: index,
          label: breakInfo.label || `Size ${index + 1}`,
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          size: breakInfo.size,
          editable: true,
        });
      });
    }

    return { colorItems, sizeItems };
  }, []);

  // Initialize state from layerConfig
  useEffect(() => {
    if (layerConfig && isOpen && is2VariableMap) {
      console.log('[TwoVariableLegendEditor] Initializing with config:', layerConfig);

      const title = layerConfig.title || layerConfig.name || "";
      setLegendTitle(title);

      // Get color and size labels
      const newColorLabel = layerConfig.colorLabel || layerConfig.valueColumn1 || "";
      const newSizeLabel = layerConfig.sizeLabel || layerConfig.valueColumn2 || "";
      setColorLabel(newColorLabel);
      setSizeLabel(newSizeLabel);

      // Get custom legend section titles
      const newColorLegendTitle = layerConfig.colorLegendTitle || "";
      const newSizeLegendTitle = layerConfig.sizeLegendTitle || "";
      setColorLegendTitle(newColorLegendTitle);
      setSizeLegendTitle(newSizeLegendTitle);

      // Get format information for color (from valueFormat)
      const valueFormat = layerConfig.valueFormat || {};
      const newColorPrefix = valueFormat.prefix || "";
      const newColorSuffix = valueFormat.suffix || "";
      const newColorDecimals = valueFormat.decimals !== undefined ? valueFormat.decimals : 2;
      setColorPrefix(newColorPrefix);
      setColorSuffix(newColorSuffix);
      setColorDecimals(newColorDecimals);

      // Get format information for size (from sizeValueFormat)
      const sizeValueFormat = layerConfig.sizeValueFormat || {};
      const newSizePrefix = sizeValueFormat.prefix || "";
      const newSizeSuffix = sizeValueFormat.suffix || "";
      const newSizeDecimals = sizeValueFormat.decimals !== undefined ? sizeValueFormat.decimals : 2;
      setSizePrefix(newSizePrefix);
      setSizeSuffix(newSizeSuffix);
      setSizeDecimals(newSizeDecimals);

      // Extract legend items
      const { colorItems, sizeItems } = extractLegendItems(layerConfig);
      setColorLegendItems(colorItems);
      setSizeLegendItems(sizeItems);

      // Store original values for reset
      originalValuesRef.current = {
        title,
        colorLabel: newColorLabel,
        sizeLabel: newSizeLabel,
        colorLegendTitle: newColorLegendTitle,
        sizeLegendTitle: newSizeLegendTitle,
        colorPrefix: newColorPrefix,
        colorSuffix: newColorSuffix,
        colorDecimals: newColorDecimals,
        sizePrefix: newSizePrefix,
        sizeSuffix: newSizeSuffix,
        sizeDecimals: newSizeDecimals,
        colorItems: JSON.parse(JSON.stringify(colorItems)),
        sizeItems: JSON.parse(JSON.stringify(sizeItems)),
      };

      setHasUnsavedChanges(false);
    }
  }, [layerConfig, isOpen, is2VariableMap, extractLegendItems]);

  // Track changes
  useEffect(() => {
    if (originalValuesRef.current.title !== undefined) {
      const hasChanges =
        legendTitle !== originalValuesRef.current.title ||
        colorLabel !== originalValuesRef.current.colorLabel ||
        sizeLabel !== originalValuesRef.current.sizeLabel ||
        colorLegendTitle !== originalValuesRef.current.colorLegendTitle ||
        sizeLegendTitle !== originalValuesRef.current.sizeLegendTitle ||
        colorPrefix !== originalValuesRef.current.colorPrefix ||
        colorSuffix !== originalValuesRef.current.colorSuffix ||
        colorDecimals !== originalValuesRef.current.colorDecimals ||
        sizePrefix !== originalValuesRef.current.sizePrefix ||
        sizeSuffix !== originalValuesRef.current.sizeSuffix ||
        sizeDecimals !== originalValuesRef.current.sizeDecimals ||
        JSON.stringify(colorLegendItems) !== JSON.stringify(originalValuesRef.current.colorItems) ||
        JSON.stringify(sizeLegendItems) !== JSON.stringify(originalValuesRef.current.sizeItems);
      setHasUnsavedChanges(hasChanges);
    }
  }, [legendTitle, colorLabel, sizeLabel, colorLegendTitle, sizeLegendTitle, colorPrefix, colorSuffix, colorDecimals,
      sizePrefix, sizeSuffix, sizeDecimals, colorLegendItems, sizeLegendItems]);

  // Handle legend item label changes
  const handleLegendItemChange = (id, newLabel, mode) => {
    if (mode === "color") {
      setColorLegendItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, label: newLabel } : item))
      );
    } else {
      setSizeLegendItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, label: newLabel } : item))
      );
    }
  };

  // Reset to original values
  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to reset all changes? This cannot be undone."
      )
    ) {
      setLegendTitle(originalValuesRef.current.title);
      setColorLabel(originalValuesRef.current.colorLabel);
      setSizeLabel(originalValuesRef.current.sizeLabel);
      setColorLegendTitle(originalValuesRef.current.colorLegendTitle);
      setSizeLegendTitle(originalValuesRef.current.sizeLegendTitle);
      setColorPrefix(originalValuesRef.current.colorPrefix);
      setColorSuffix(originalValuesRef.current.colorSuffix);
      setColorDecimals(originalValuesRef.current.colorDecimals);
      setSizePrefix(originalValuesRef.current.sizePrefix);
      setSizeSuffix(originalValuesRef.current.sizeSuffix);
      setSizeDecimals(originalValuesRef.current.sizeDecimals);
      setColorLegendItems(
        JSON.parse(JSON.stringify(originalValuesRef.current.colorItems))
      );
      setSizeLegendItems(
        JSON.parse(JSON.stringify(originalValuesRef.current.sizeItems))
      );
      setHasUnsavedChanges(false);
    }
  };

  // Debounce ref for preview
  const previewTimeoutRef = useRef(null);
  const lastPreviewConfigRef = useRef(null);

  // Trigger live preview when legend properties change (debounced)
  const triggerPreview = useCallback(() => {
    if (!onPreview || !layerConfig) return;

    // Clear any pending preview
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    // Debounce the preview to prevent excessive updates
    previewTimeoutRef.current = setTimeout(() => {
      // Build preview config with current legend values
      const previewConfig = {
        ...layerConfig,
        title: legendTitle,
        colorLabel: colorLabel,
        sizeLabel: sizeLabel,
        colorLegendTitle: colorLegendTitle,
        sizeLegendTitle: sizeLegendTitle,
        valueFormat: {
          prefix: colorPrefix,
          suffix: colorSuffix,
          decimals: parseInt(colorDecimals, 10),
        },
        sizeValueFormat: {
          prefix: sizePrefix,
          suffix: sizeSuffix,
          decimals: parseInt(sizeDecimals, 10),
        },
        // Flag to indicate this is a legend-only preview - don't touch the map layer
        isLegendOnlyPreview: true,
      };

      // Update color legend items
      if (previewConfig.colorClassBreakInfos) {
        previewConfig.colorClassBreakInfos = previewConfig.colorClassBreakInfos.map(
          (breakInfo, index) => {
            const item = colorLegendItems.find((i) => i.id === index);
            return { ...breakInfo, label: item ? item.label : breakInfo.label };
          }
        );
      }

      // Update size legend items
      if (previewConfig.sizeClassBreakInfos) {
        previewConfig.sizeClassBreakInfos = previewConfig.sizeClassBreakInfos.map(
          (breakInfo, index) => {
            const item = sizeLegendItems.find((i) => i.id === index);
            return { ...breakInfo, label: item ? item.label : breakInfo.label };
          }
        );
      }

      // Avoid duplicate previews with same config
      const configKey = JSON.stringify({
        title: previewConfig.title,
        colorLegendTitle: previewConfig.colorLegendTitle,
        sizeLegendTitle: previewConfig.sizeLegendTitle,
        valueFormat: previewConfig.valueFormat,
        sizeValueFormat: previewConfig.sizeValueFormat,
      });
      if (lastPreviewConfigRef.current === configKey) {
        return;
      }
      lastPreviewConfigRef.current = configKey;

      console.log('[TwoVariableLegendEditor] Triggering live preview');
      onPreview(previewConfig);
    }, 500); // 500ms debounce
  }, [onPreview, layerConfig, legendTitle, colorLabel, sizeLabel, colorLegendTitle, sizeLegendTitle,
      colorPrefix, colorSuffix, colorDecimals, sizePrefix, sizeSuffix, sizeDecimals,
      colorLegendItems, sizeLegendItems]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, []);

  // Trigger preview when any legend property changes
  useEffect(() => {
    if (hasUnsavedChanges) {
      triggerPreview();
    }
  }, [hasUnsavedChanges, triggerPreview]);

  const handleSave = async () => {
    if (!layerConfig) {
      console.warn("Cannot save: No layer configuration available");
      return;
    }

    setIsSaving(true);

    try {
      // Create updated config with new legend properties
      const updatedConfig = {
        ...layerConfig,
        title: legendTitle,
        colorLabel: colorLabel,
        sizeLabel: sizeLabel,
        colorLegendTitle: colorLegendTitle,
        sizeLegendTitle: sizeLegendTitle,
        valueFormat: {
          prefix: colorPrefix,
          suffix: colorSuffix,
          decimals: parseInt(colorDecimals, 10),
        },
        sizeValueFormat: {
          prefix: sizePrefix,
          suffix: sizeSuffix,
          decimals: parseInt(sizeDecimals, 10),
        },
      };

      // Use user's manually edited labels from the legend items
      // The labels in colorLegendItems/sizeLegendItems reflect what the user sees in the preview
      updatedConfig.colorClassBreakInfos = updatedConfig.colorClassBreakInfos.map(
        (breakInfo, index) => {
          const item = colorLegendItems.find((i) => i.id === index);
          // Use the user's edited label directly - don't regenerate
          const newLabel = item ? item.label : breakInfo.label;
          return { ...breakInfo, label: newLabel };
        }
      );

      // Use user's manually edited labels for size breaks
      updatedConfig.sizeClassBreakInfos = updatedConfig.sizeClassBreakInfos.map(
        (breakInfo, index) => {
          const item = sizeLegendItems.find((i) => i.id === index);
          // Use the user's edited label directly - don't regenerate
          const newLabel = item ? item.label : breakInfo.label;
          return { ...breakInfo, label: newLabel };
        }
      );

      // Call onConfigChange to update the configuration
      if (onConfigChange) {
        onConfigChange(updatedConfig);
      }

      // Call onSave if provided (for additional save logic)
      if (onSave) {
        await onSave(updatedConfig);
      }

      // Update original values
      originalValuesRef.current = {
        title: legendTitle,
        colorLabel: colorLabel,
        sizeLabel: sizeLabel,
        colorLegendTitle: colorLegendTitle,
        sizeLegendTitle: sizeLegendTitle,
        colorPrefix: colorPrefix,
        colorSuffix: colorSuffix,
        colorDecimals: colorDecimals,
        sizePrefix: sizePrefix,
        sizeSuffix: sizeSuffix,
        sizeDecimals: sizeDecimals,
        colorItems: JSON.parse(JSON.stringify(colorLegendItems)),
        sizeItems: JSON.parse(JSON.stringify(sizeLegendItems)),
      };
      setHasUnsavedChanges(false);

      // CRITICAL: Trigger final preview with saved config BEFORE closing
      // This ensures customLegendContent is updated with the final saved configuration
      // so the actual legend reflects the saved changes, not stale preview data
      if (onPreview) {
        console.log('[TwoVariableLegendEditor] Triggering final preview before close with saved config');
        onPreview(updatedConfig);
      }

      // Close the editor
      onClose();
    } catch (error) {
      console.error("Error saving legend properties:", error);
      alert("Failed to save legend properties. Check console for details.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        if (onClose) onClose();
      }
    } else {
      if (onClose) onClose();
    }
  };

  // Get color string from various formats
  const getColorString = (color) => {
    if (!color) return "#808080";
    if (typeof color === "string") return color;
    if (Array.isArray(color) && color.length >= 3) {
      const [r, g, b, a = 1] = color;
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
    }
    return "#808080";
  };

  // Don't render if not a 2-variable map
  if (!is2VariableMap) {
    return null;
  }

  const currentLegendItems = editMode === "color" ? colorLegendItems : sizeLegendItems;
  const currentPrefix = editMode === "color" ? colorPrefix : sizePrefix;
  const currentSuffix = editMode === "color" ? colorSuffix : sizeSuffix;
  const currentDecimals = editMode === "color" ? colorDecimals : sizeDecimals;

  return (
    <div
      className={`
      fixed inset-y-0 right-0 z-30 w-[500px] h-full flex flex-col bg-white dark:bg-gray-800 shadow-xl
      transform transition-transform duration-300 ease-in-out border-l border-gray-200 dark:border-gray-700
      ${isOpen ? "translate-x-0" : "translate-x-full"}
    `}
    >
      {/* Header */}
      <div className="flex-none h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Edit 2-Variable Legend
          </h2>
          {hasUnsavedChanges && (
            <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded">
              Unsaved
            </span>
          )}
        </div>
      </div>

      {/* Split Button Toggle */}
      <div className="flex-none px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => setEditMode("color")}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 font-medium transition-colors ${
              editMode === "color"
                ? "bg-blue-600 text-white"
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
            }`}
          >
            <Palette size={18} />
            <span>Edit Color Legend</span>
          </button>
          <button
            onClick={() => setEditMode("size")}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
              editMode === "size"
                ? "bg-blue-600 text-white"
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
            }`}
          >
            <Ruler size={18} />
            <span>Edit Size Legend</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Mode-specific Label */}
          <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {editMode === "color" ? (
              <>
                <Palette size={16} className="text-blue-600" />
                <span>Editing Color Legend ({colorLabel || "Color"})</span>
              </>
            ) : (
              <>
                <Ruler size={16} className="text-blue-600" />
                <span>Editing Size Legend ({sizeLabel || "Size"})</span>
              </>
            )}
          </div>

          {/* Legend Section Title - Only for current mode */}
          <div className="space-y-2">
            <label
              htmlFor="legend-section-title"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {editMode === "color" ? "Color Legend Title" : "Size Legend Title"}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              id="legend-section-title"
              type="text"
              value={editMode === "color" ? colorLegendTitle : sizeLegendTitle}
              onChange={(e) =>
                editMode === "color"
                  ? setColorLegendTitle(e.target.value)
                  : setSizeLegendTitle(e.target.value)
              }
              placeholder={
                editMode === "color"
                  ? `e.g., Color: ${colorLabel || 'Variable 1'}`
                  : `e.g., Size: ${sizeLabel || 'Variable 2'}`
              }
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                (editMode === "color" ? !colorLegendTitle : !sizeLegendTitle)
                  ? "border-red-300 dark:border-red-600"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            />
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Enter a legend title, or leave empty for no section title.
            </p>
          </div>

          {/* Value Format Section */}
          <div className="space-y-4 border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
                Value Format
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                For {editMode === "color" ? "color" : "size"} values
              </span>
            </div>

            {/* Prefix */}
            <div>
              <label
                htmlFor="format-prefix"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Prefix
              </label>
              <input
                id="format-prefix"
                type="text"
                value={currentPrefix}
                onChange={(e) =>
                  editMode === "color"
                    ? setColorPrefix(e.target.value)
                    : setSizePrefix(e.target.value)
                }
                placeholder="e.g., $"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Suffix */}
            <div>
              <label
                htmlFor="format-suffix"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Suffix
              </label>
              <input
                id="format-suffix"
                type="text"
                value={currentSuffix}
                onChange={(e) =>
                  editMode === "color"
                    ? setColorSuffix(e.target.value)
                    : setSizeSuffix(e.target.value)
                }
                placeholder="e.g., %, /sq mi"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Decimal Places */}
            <div>
              <label
                htmlFor="format-decimals"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Decimal Places
              </label>
              <input
                id="format-decimals"
                type="number"
                min="0"
                max="10"
                value={currentDecimals}
                onChange={(e) =>
                  editMode === "color"
                    ? setColorDecimals(parseInt(e.target.value, 10))
                    : setSizeDecimals(parseInt(e.target.value, 10))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Format Preview */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md">
              <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">
                Format Preview
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Single: {formatValue(1234.57, editMode)}
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Range: {formatValue(1234.57, editMode, false)} - {formatValue(5678.90, editMode, true)}
              </p>
            </div>
          </div>

          {/* Legend Items Section */}
          {currentLegendItems.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
                  {editMode === "color" ? "Color" : "Size"} Legend Items
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {currentLegendItems.length} items
                </span>
              </div>

              <div className="space-y-3">
                {currentLegendItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md"
                  >
                    {/* Visual indicator */}
                    {editMode === "color" ? (
                      <div
                        className="w-6 h-6 rounded flex-shrink-0 mt-1 border border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: getColorString(item.color) }}
                      />
                    ) : (
                      <div
                        className="rounded-full flex-shrink-0 mt-1 border border-gray-300 dark:border-gray-600 bg-gray-400"
                        style={{
                          width: `${Math.min(28, Math.max(8, item.size))}px`,
                          height: `${Math.min(28, Math.max(8, item.size))}px`,
                        }}
                      />
                    )}

                    {/* Label editor */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) =>
                          handleLegendItemChange(item.id, e.target.value, editMode)
                        }
                        disabled={!item.editable}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {item.minValue !== undefined && item.maxValue !== undefined && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Range: {formatValue(item.minValue, editMode)} - {formatValue(item.maxValue, editMode)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Section - Only show current variable's legend */}
          <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
              {editMode === "color" ? "Color Legend Preview" : "Size Legend Preview"}
            </h3>
            <div className="bg-white border border-gray-300 rounded-md shadow-md p-4" style={{maxWidth: '320px'}}>
              {editMode === "color" ? (
                /* Color Section */
                <div>
                  {colorLegendTitle && (
                    <h5 className="text-xs font-semibold mb-1.5 text-gray-700">
                      {colorLegendTitle}
                    </h5>
                  )}
                  <div className="space-y-1">
                    {colorLegendItems.map((item) => (
                      <div key={`preview-color-${item.id}`} className="flex items-center">
                        <div
                          className="w-3 h-3 mr-1.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: getColorString(item.color),
                            border: '1px solid #FFFFFF'
                          }}
                        />
                        <span className="text-xs text-gray-700 truncate">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Size Section */
                <div>
                  {sizeLegendTitle && (
                    <h5 className="text-xs font-semibold mb-1.5 text-gray-700">
                      {sizeLegendTitle}
                    </h5>
                  )}
                  <div className="space-y-1">
                    {sizeLegendItems.map((item) => (
                      <div key={`preview-size-${item.id}`} className="flex items-center">
                        <div
                          className="mr-1.5 rounded-full flex-shrink-0 bg-gray-400"
                          style={{
                            width: `${Math.round(item.size)}px`,
                            height: `${Math.round(item.size)}px`,
                            border: '1px solid #666'
                          }}
                        />
                        <span className="text-xs text-gray-700 truncate">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-none p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex justify-between items-center space-x-3">
          {/* Reset Button */}
          <button
            onClick={handleReset}
            disabled={!hasUnsavedChanges}
            className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reset all changes to original values"
          >
            <RotateCcw size={16} />
            <span>Reset</span>
          </button>

          <div className="flex space-x-3">
            {/* Cancel Button */}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={16} className={isSaving ? "animate-spin" : ""} />
              <span>{isSaving ? "Saving..." : "Apply Changes"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

TwoVariableLegendEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  layerConfig: PropTypes.object,
  onConfigChange: PropTypes.func,
  onSave: PropTypes.func,
  onPreview: PropTypes.func,
};

export default TwoVariableLegendEditor;
